import { ChevronDown, ChevronUp, X } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, useDiscardGuard,
} from "@/components/ui/dialog";
import { Callout, Skeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SkillChoice } from "@/features/user-skills";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useFormChanged, zodResolver } from "@/lib/form";
import { INVITATION_CSV_TEMPLATE, MAX_CSV_INVITATIONS, parseInvitationCsv, singleInviteDomain } from "@/lib/invitation-csv";
import {
  MAX_BULK_INVITATIONS, addAddresses, bulkInvitationPayload, invitationPayload, nameFromEmail,
  personSummary, personTerms, phoneDomainOf, scopeForRole, soleTenantId, staffable,
  type BulkPerson, type PersonTerms,
} from "@/lib/invitations";
import {
  useCampaigns, useCreateInvitation, useCreateInvitations, useDomains, useSkills,
} from "@/lib/queries";
import { assignableSkills } from "@/lib/skills";
import {
  ROLE_DESCRIPTIONS, ROLE_LABELS, assignableRoles, type Campaign, type InvitationBulkSent,
  type InvitationSent, type Role, type RoutingSkill,
} from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Invite somebody (ADR-028 in `platform`): who they will be is decided here, and made when they
 * accept.
 *
 * The single configured domain is selected automatically: reps and supervisors are scoped to
 * it, while admins span the tenant and receive a phone in it. The server still enforces scope.
 * There is no password and no extension. The person chooses the password, and their phone gets the
 * lowest free extension when they accept.
 *
 * Campaigns and skills attach to the phone the ACD rings, so they are offered only where the account
 * will have one, and never for a superadmin, who belongs to no tenant's catalog. A campaign means its
 * primary line, the one the Staffing tab's "Add a rep" starts on.
 *
 * "Paste emails" invites up to 20 at once (`POST /invitations/bulk`). Addresses are pasted or typed
 * into a box and moved onto a list, each with a name read from the address that the admin can
 * change. The role, campaigns and skills below the list are what everybody joins on, and any row
 * can be opened and given its own instead: a floor of reps with a supervisor on it is one paste.
 * A row's role brings the scope that role needs with it, since the list's belongs to the list's
 * role. An address the server finds taken is skipped, and the answer says so.
 */

const schema = z
  .object({
    mode: z.enum(["one", "several", "csv"]),
    email: z.string(),
    display_name: z.string().max(200),
    role: z.enum(["superadmin", "admin", "supervisor", "rep"]),
    domain_id: z.number().nullable(),
    endpoint_domain_id: z.number().nullable(),
  })
  .superRefine((value, ctx) => {
    // The list of several people is checked on its own, outside the form's values.
    if (value.mode === "one" && value.email.trim() === "") {
      ctx.addIssue({ code: "custom", path: ["email"], message: "Required" });
    } else if (value.mode === "one" && !z.string().email().safeParse(value.email.trim()).success) {
      ctx.addIssue({ code: "custom", path: ["email"], message: "That does not look like an email" });
    }
    if ((value.role === "supervisor" || value.role === "rep") && value.domain_id === null) {
      ctx.addIssue({
        code: "custom",
        path: ["domain_id"],
        message: `A ${ROLE_LABELS[value.role].toLowerCase()} must be locked to a domain`,
      });
    }
  });

type FormValues = z.infer<typeof schema>;

const MODE_DESCRIPTIONS: Record<FormValues["mode"], string> = {
  one: "They choose their own password. The link works once, for 72 hours, and they are on their campaigns with their skills the moment they accept.",
  csv: "Upload names, emails, roles, campaign slugs and skill levels. Review the rows, then send setup links. Accounts are created when users accept.",
  several: `Up to ${MAX_BULK_INVITATIONS} at once, on the role, campaigns and skills below unless a row is given its own. Review them before sending. Each gets their own link, which works once, for 72 hours.`,
};

export function InviteUserDialog({
  open, onOpenChange, onSent, onSentMany,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The invitation just sent, with its link: the page shows it. */
  onSent: (sent: InvitationSent) => void;
  /** The answer to inviting several people, with each one's link or why they were skipped. */
  onSentMany: (sent: InvitationBulkSent) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        {/* Radix unmounts the content when the dialog closes, so each opening starts a blank form. */}
        <InviteForm onDone={() => onOpenChange(false)} onSent={onSent} onSentMany={onSentMany} />
      </DialogContent>
    </Dialog>
  );
}

function InviteForm({
  onDone, onSent, onSentMany,
}: {
  onDone: () => void;
  onSent: (sent: InvitationSent) => void;
  onSentMany: (sent: InvitationBulkSent) => void;
}) {
  const { user: actor } = useAuth();
  const create = useCreateInvitation();
  const createMany = useCreateInvitations();
  const domainQuery = useDomains();
  const domains = domainQuery.data;
  const campaigns = useCampaigns();
  const catalog = useSkills();
  const roles = assignableRoles(actor?.role);
  const [formError, setFormError] = React.useState<string | null>(null);
  const [picked, setPicked] = React.useState<string[]>([]);
  const [skills, setSkills] = React.useState<Record<string, number>>({});
  const [people, setPeople] = React.useState<BulkPerson[]>([]);
  const [csvText, setCsvText] = React.useState("");
  const [csvFile, setCsvFile] = React.useState("");
  const [fileProblem, setFileProblem] = React.useState<string | null>(null);
  const [readingFile, setReadingFile] = React.useState(false);
  const fileVersion = React.useRef(0);
  const [draft, setDraft] = React.useState("");
  const [draftProblem, setDraftProblem] = React.useState<string | null>(null);
  // One row is opened at a time: the drawer is narrow, and a row's terms are as tall as the list's.
  const [openRow, setOpenRow] = React.useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      mode: "one", email: "", display_name: "", role: (roles.at(-1) ?? "rep") as Role,
      domain_id: null, endpoint_domain_id: null,
    },
  });

  const mode = form.watch("mode");
  const several = mode === "several";
  const csv = mode === "csv";

  // Tabs and automatically derived domain fields hold no user edits. Domain loading must not
  // trigger a discard warning; role and all user-entered values still count as changes.
  const formChanged = useFormChanged(form, ["mode", "domain_id", "endpoint_domain_id"]);
  useDiscardGuard(
    formChanged || picked.length > 0 || Object.keys(skills).length > 0 || people.length > 0 ||
      draft.trim() !== "" || csvFile !== "",
  );
  const role = form.watch("role");

  const domainId = singleInviteDomain(domains ?? []);
  React.useEffect(() => {
    const scope = scopeForRole(role, domainId);
    form.setValue("domain_id", scope.domain_id);
    form.setValue("endpoint_domain_id", scope.endpoint_domain_id);
  }, [domainId, role, form]);

  // An admin's own tenant; a superadmin invites into the sole tenant, which is the one the rows share.
  const tenantId = actor?.tenant_id ?? soleTenantId([...(campaigns.data ?? []), ...(catalog.data ?? [])]);
  const tenantCampaigns = (campaigns.data ?? [])
    .filter((c) => c.tenant_id === tenantId)
    .sort((a, b) => a.name.localeCompare(b.name));
  const assignable = assignableSkills(catalog.data ?? [], tenantId);
  const csvPreview = parseInvitationCsv(csvText, tenantCampaigns, assignable, roles);
  const referencesReady = !campaigns.isPending && !catalog.isPending &&
    !campaigns.error && !catalog.error;
  const scopeReady = role === "superadmin" || domainId !== null;
  const csvBlocked = csv && (!csvFile || readingFile || !!fileProblem ||
    csvPreview.errors.length > 0 || !referencesReady || role === "superadmin");

  const readCsv = async (file: File | undefined) => {
    const version = ++fileVersion.current;
    setCsvText("");
    setCsvFile(file?.name ?? "");
    setFileProblem(null);
    setReadingFile(false);
    if (!file) return;
    if (!/\.csv$/i.test(file.name) || file.size > 1_000_000) {
      setFileProblem("Choose a CSV file no larger than 1 MB.");
      return;
    }
    setReadingFile(true);
    try {
      const text = await file.text();
      if (version === fileVersion.current) setCsvText(text);
    } catch {
      if (version === fileVersion.current) setFileProblem("Could not read this file. Choose it again.");
    } finally {
      if (version === fileVersion.current) setReadingFile(false);
    }
  };

  /** The role, campaigns and skills below the list: everybody's, unless a row has its own. */
  const shared: PersonTerms = {
    role,
    domain_id: form.watch("domain_id"),
    endpoint_domain_id: form.watch("endpoint_domain_id"),
    campaign_ids: picked,
    skills,
  };
  const setShared = (next: PersonTerms) => {
    if (next.role !== role) form.setValue("role", next.role, { shouldValidate: true });
    setPicked([...next.campaign_ids]);
    setSkills({ ...next.skills });
  };

  /** Gives one person terms of their own, or — with null — puts them back on the list's. */
  const setPersonTerms = (email: string, next: PersonTerms | null) =>
    setPeople((current) => current.map((p) => (p.email === email ? { ...p, terms: next } : p)));

  /** Moves what the box holds onto the list, leaving behind what could not go, and says why. */
  const addDraft = () => {
    const next = addAddresses(people, draft);
    setPeople(next.people);
    setDraft(next.left);
    setDraftProblem(next.problem);
    return next;
  };

  const renamePerson = (email: string, display_name: string) =>
    setPeople((current) => current.map((p) => (p.email === email ? { ...p, display_name } : p)));
  const removePerson = (email: string) => {
    setPeople((current) => current.filter((p) => p.email !== email));
    setOpenRow((open) => (open === email ? null : open));
  };

  // An address already typed for one person starts the list of several.
  const switchMode = (next: FormValues["mode"]) => {
    if (next === "several") {
      const email = form.getValues("email").trim();
      if (email !== "" && people.length === 0) {
        const typed = form.getValues("display_name").trim();
        const added = addAddresses([], email);
        setPeople(added.people.map((p) => ({ ...p, display_name: typed || nameFromEmail(p.email) })));
      }
      form.clearErrors("email");
    }
    setFormError(null);
    form.setValue("mode", next);
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    if (!scopeReady || csvBlocked) return;
    const terms = { ...values, campaign_ids: picked, skills };
    try {
      if (values.mode === "csv") {
        const base = bulkInvitationPayload([], { ...terms, campaign_ids: [], skills: {} }, assignable);
        const sent = await createMany.mutateAsync({
          ...base,
          source: "csv",
          // A row that named a role brings the scope that role needs, as a pasted row's does.
          people: csvPreview.people.map(({ email, display_name, role: own, campaign_ids, skills }) => ({
            email, display_name, campaign_ids, skills,
            ...(own ? { role: own, ...scopeForRole(own, domainId) } : {}),
          })),
        });
        onDone();
        onSentMany(sent);
        return;
      }
      if (values.mode === "several") {
        // Whatever is still in the box goes too, so nobody typed but not yet added is left out.
        const { people: everyone, problem } = addDraft();
        if (problem) return;
        if (everyone.length === 0) {
          setDraftProblem("Add at least one email address.");
          return;
        }
        const sent = await createMany.mutateAsync(bulkInvitationPayload(everyone, terms, assignable));
        onDone();
        onSentMany(sent);
        return;
      }
      const sent = await create.mutateAsync(invitationPayload(terms, assignable));
      onDone();
      onSent(sent);
    } catch (e) {
      setFormError(
        e instanceof ApiError
          ? e.message
          : several || csv ? "Could not send the invitations." : "Could not send the invitation.",
      );
    }
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{csv ? "Invite users from CSV" : several ? "Invite several people" : "Invite a user"}</DialogTitle>
        <DialogDescription className="grid min-h-[3lh]">
          {/* Shared grid cell reserves the longest description's height at every screen width. */}
          {Object.entries(MODE_DESCRIPTIONS).map(([key, description]) => (
            <span key={key} aria-hidden={key !== mode}
              className={cn("col-start-1 row-start-1", key !== mode && "invisible")}>
              {description}
            </span>
          ))}
        </DialogDescription>
      </DialogHeader>

      <DialogForm onSubmit={onSubmit} noValidate>
        <DialogBody className="space-y-4">
          <Tabs value={mode} onValueChange={(next) => switchMode(next as FormValues["mode"])}>
            <TabsList aria-label="Who to invite">
              <TabsTrigger value="one">One person</TabsTrigger>
              <TabsTrigger value="csv">Upload CSV</TabsTrigger>
              <TabsTrigger value="several">Paste emails</TabsTrigger>
            </TabsList>
          </Tabs>

          {formError && <Callout tone="danger">{formError}</Callout>}

          {csv ? (
            <section className="space-y-3">
              <a
                className="text-sm text-accent-11 hover:underline"
                href={`data:text/csv;charset=utf-8,${encodeURIComponent(INVITATION_CSV_TEMPLATE)}`}
                download="user-invitations-template.csv"
              >Download CSV template</a>
              <Field label="CSV file" htmlFor="invite-csv" required
                hint={`Up to ${MAX_CSV_INVITATIONS} people. Columns: name, email, role, skills, campaign_slug. A row with no role takes the one below. Use semicolons for multiple campaigns or skills, e.g. setter:4;spanish:2. Replace the template slugs with your own.`}>
                <Input type="file" accept=".csv,text/csv" disabled={form.formState.isSubmitting}
                  onChange={(e) => void readCsv(e.target.files?.[0])} />
              </Field>
              {!referencesReady && !campaigns.error && !catalog.error && <p className="text-sm text-text-muted">Loading campaigns and skills…</p>}
              {readingFile && <p className="text-sm text-text-muted">Reading CSV…</p>}
              {fileProblem && <Callout tone="danger">{fileProblem}</Callout>}
              {csvFile && !readingFile && !fileProblem && referencesReady && csvPreview.errors.length > 0 && (
                <Callout tone="danger"><ul>{csvPreview.errors.map((error, i) => <li key={i}>{error}</li>)}</ul></Callout>
              )}
              {csvFile && !readingFile && !fileProblem && referencesReady && csvPreview.errors.length === 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-text-muted">{csvPreview.people.length} people ready to invite</p>
                  <ul className="max-h-80 divide-y divide-border overflow-y-auto rounded-md border border-border" aria-label="CSV preview">
                    {csvPreview.people.map((p) => (
                      <li key={p.email} className="space-y-1 px-3 py-2 text-sm">
                        <p className="font-medium text-text">{p.display_name} · {p.email}</p>
                        <p className="text-xs text-text-muted">
                          Role: {p.role ? ROLE_LABELS[p.role] : `${ROLE_LABELS[role]} (from above)`}
                        </p>
                        <p className="text-xs text-text-muted">Campaigns: {p.campaign_slugs.join(", ") || "None"}</p>
                        <p className="text-xs text-text-muted">Skills: {p.skills.map((s) => `${s.slug}: ${s.level}`).join(", ") || "None"}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          ) : several ? (
            <section className="space-y-2">
              <Field
                label="Email addresses"
                htmlFor="invite-emails"
                required
                hint="Paste or type them, separated by commas or new lines, and press Enter to add them."
                error={draftProblem ?? undefined}
                action={
                  <Button type="button" variant="outline" size="sm" onClick={addDraft} disabled={draft.trim() === ""}>
                    Add
                  </Button>
                }
              >
                <Textarea
                  rows={3}
                  value={draft}
                  autoComplete="off"
                  spellCheck={false}
                  invalid={Boolean(draftProblem)}
                  placeholder={"jane.doe@example.com, john.smith@example.com"}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    setDraftProblem(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      addDraft();
                    }
                  }}
                  onBlur={() => {
                    if (draft.trim() !== "") addDraft();
                  }}
                />
              </Field>

              <div className="flex items-baseline justify-between gap-2">
                <p className="text-xs text-text-muted">
                  Names are read from the address, so john.smith is John Smith. Change any, or clear it
                  to let them fill it in.
                </p>
                <span className="shrink-0 text-xs tabular-nums text-text-muted" aria-live="polite">
                  {people.length} of {MAX_BULK_INVITATIONS}
                </span>
              </div>
              {people.length === 0 ? (
                <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-text-muted">
                  Nobody on the list yet.
                </p>
              ) : (
                <ul
                  aria-label="People to invite"
                  className="max-h-[32rem] divide-y divide-border overflow-y-auto rounded-md border border-border"
                >
                  {people.map((person) => {
                    const own = person.terms !== null && person.terms !== undefined;
                    const theirs = personTerms(person, shared);
                    const summary = personSummary(theirs, tenantCampaigns, assignable);
                    const open = openRow === person.email;
                    return (
                      <li key={person.email} className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="min-w-40 flex-1 truncate font-mono text-xs text-text" title={person.email}>
                            {person.email}
                          </span>
                          <span className="flex w-full items-center gap-1 sm:w-auto">
                            <Input
                              aria-label={`Name for ${person.email}`}
                              autoComplete="off"
                              className="sm:w-44"
                              placeholder="No name"
                              maxLength={200}
                              value={person.display_name}
                              onChange={(e) => renamePerson(person.email, e.target.value)}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`Remove ${person.email}`}
                              onClick={() => removePerson(person.email)}
                            >
                              <X />
                            </Button>
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="-ml-2 h-6 min-w-0 shrink gap-1 px-2 text-xs font-normal text-text-muted"
                            aria-expanded={open}
                            aria-label={`${open ? "Close" : "Open"} the terms for ${person.email}`}
                            title={summary}
                            onClick={() => setOpenRow(open ? null : person.email)}
                          >
                            <span className="truncate">{summary}</span>
                            {open ? <ChevronUp className="shrink-0" /> : <ChevronDown className="shrink-0" />}
                          </Button>
                          {own ? (
                            <>
                              <Badge tone="accent" size="sm">Their own</Badge>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs font-normal text-accent-11"
                                onClick={() => setPersonTerms(person.email, null)}
                              >
                                Use the list's
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-text-muted">From the list</span>
                          )}
                        </div>
                        {open && (
                          <div className="mt-2 space-y-3 rounded-md border border-border bg-surface-sunken p-3">
                            <TermsFields
                              idPrefix={`invite-person-${person.email}`}
                              label={person.display_name.trim() || person.email}
                              terms={theirs}
                              roles={roles}
                              domainId={domainId}
                              onChange={(next) => setPersonTerms(person.email, next)}
                              tenantCampaigns={tenantCampaigns}
                              assignable={assignable}
                              campaignsPending={campaigns.isPending}
                              catalogPending={catalog.isPending}
                            />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Email" htmlFor="invite-email" required error={form.formState.errors.email?.message}>
                <Input
                  type="email"
                  autoComplete="off"
                  invalid={Boolean(form.formState.errors.email)}
                  {...form.register("email")}
                />
              </Field>
              <Field label="Name" htmlFor="invite-name" hint="Optional. They can change it when they accept.">
                <Input autoComplete="off" {...form.register("display_name")} />
              </Field>
            </div>
          )}

          {several && people.length > 0 && (
            <p className="text-xs font-medium text-text">Everybody on the list</p>
          )}
          <TermsFields
            idPrefix="invite"
            terms={shared}
            roles={roles}
            domainId={domainId}
            onChange={setShared}
            tenantCampaigns={tenantCampaigns}
            assignable={assignable}
            campaignsPending={campaigns.isPending}
            catalogPending={catalog.isPending}
            /* A CSV row carries its own campaigns and skills, so only the role is shared. */
            roleOnly={csv}
          />

          {!scopeReady && (
            <Callout tone="info">
              {domainQuery.isPending ? "Loading account setup…" : domainQuery.error
                ? "Could not load account setup. Close and reopen this form to try again."
                : "Invitations require exactly one configured domain. Ask an administrator to check the platform setup."}
            </Callout>
          )}
          {(campaigns.error || catalog.error) && (
            <Callout tone="danger">Could not load campaigns or skills. Close and reopen this form to try again.</Callout>
          )}
          {csv && role === "superadmin" && <Callout tone="info">CSV invitations support tenant roles. Choose admin, supervisor or rep.</Callout>}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDone}>
            Cancel
          </Button>
          <Button type="submit" variant="default" loading={form.formState.isSubmitting} disabled={!scopeReady || csvBlocked}>
            {csv ? `Send ${csvPreview.people.length} invitations` : several && people.length !== 1
              ? people.length > 1 ? `Send ${people.length} invitations` : "Send invitations"
              : "Send invitation"}
          </Button>
        </DialogFooter>
      </DialogForm>
    </>
  );
}


/**
 * The role somebody joins on, and — where that role comes with a phone — the campaigns they staff
 * and the skills they hold. The list's own terms and each person's are the same fields, so both
 * render this: `idPrefix` is what keeps their checkboxes apart in one open drawer.
 */
function TermsFields({
  idPrefix, label, terms, roles, domainId, onChange, tenantCampaigns, assignable, campaignsPending,
  catalogPending, roleOnly = false,
}: {
  idPrefix: string;
  /** Whose terms these are, said in the fields' labels. Absent for the list's own. */
  label?: string;
  terms: PersonTerms;
  roles: readonly Role[];
  /** The one configured domain, which the role decides the use of. */
  domainId: number | null;
  onChange: (next: PersonTerms) => void;
  tenantCampaigns: readonly Campaign[];
  assignable: readonly RoutingSkill[];
  campaignsPending: boolean;
  catalogPending: boolean;
  /** For a CSV, where each row brings its own campaigns and skills and only the role is shared. */
  roleOnly?: boolean;
}) {
  const whose = label ? ` for ${label}` : "";
  const staffs = staffable(terms.role, phoneDomainOf(terms));
  const toggleCampaign = (id: string, on: boolean) =>
    onChange({
      ...terms,
      campaign_ids: on
        ? [...terms.campaign_ids, id]
        : terms.campaign_ids.filter((c) => c !== id),
    });
  const chooseSkill = (slug: string, level: number | null) => {
    const skills = { ...terms.skills };
    if (level === null) delete skills[slug];
    else skills[slug] = level;
    onChange({ ...terms, skills });
  };

  return (
    <>
      <Field
        label={label ? `Role${whose}` : "Role"}
        htmlFor={`${idPrefix}-role`}
        required
        hint={ROLE_DESCRIPTIONS[terms.role]}
      >
        <Select
          value={terms.role}
          onValueChange={(v) =>
            onChange({ ...terms, role: v as Role, ...scopeForRole(v as Role, domainId) })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {roles.map((r) => (
              <SelectItem key={r} value={r} description={ROLE_DESCRIPTIONS[r]}>
                {ROLE_LABELS[r]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {roleOnly ? null : staffs ? (
        <>
          <section className="space-y-2">
            <div>
              <p className="text-xs font-medium text-text">Campaigns{whose}</p>
              <p className="text-xs text-text-muted">
                They are put on each campaign's primary line when they accept.
              </p>
            </div>
            {campaignsPending ? (
              <Skeleton className="h-12" />
            ) : tenantCampaigns.length === 0 ? (
              <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-text-muted">
                No campaigns yet.
              </p>
            ) : (
              <ul className="max-h-64 divide-y divide-border overflow-y-auto rounded-md border border-border">
                {tenantCampaigns.map((campaign) => {
                  const id = `${idPrefix}-campaign-${campaign.id}`;
                  const lineless = campaign.queue_id === null;
                  return (
                    <li key={campaign.id} className="flex items-center gap-3 px-3 py-2.5">
                      <input
                        id={id}
                        type="checkbox"
                        className="size-3.5 shrink-0 accent-accent-9"
                        checked={terms.campaign_ids.includes(campaign.id)}
                        disabled={lineless}
                        onChange={(e) => toggleCampaign(campaign.id, e.target.checked)}
                      />
                      <label
                        htmlFor={id}
                        className={cn("min-w-0 flex-1", lineless ? "cursor-not-allowed" : "cursor-pointer")}
                      >
                        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                          <span className="text-sm font-medium text-text">{campaign.name}</span>
                          <span className="font-mono text-xs text-text-muted">{campaign.slug}</span>
                          {campaign.status !== "active" && (
                            <Badge tone="outline" size="sm">
                              {campaign.status}
                            </Badge>
                          )}
                        </span>
                        {lineless && (
                          <span className="block text-xs text-text-muted">It has no line to staff yet.</span>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="space-y-2">
            <div>
              <p className="text-xs font-medium text-text">Skills{whose}</p>
              <p className="text-xs text-text-muted">
                What the ACD matches their phone against. A level runs from 1 to 5.
              </p>
            </div>
            {catalogPending ? (
              <Skeleton className="h-12" />
            ) : assignable.length === 0 ? (
              <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-text-muted">
                The catalog has no rep skills to give.{" "}
                <Link to="/skills" className="text-accent-11 hover:underline">
                  Add one on Skills.
                </Link>
              </p>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {assignable.map((skill) => (
                  <SkillChoice
                    key={skill.id}
                    skill={skill}
                    idPrefix={`${idPrefix}-skill`}
                    level={terms.skills[skill.slug]}
                    onChange={(level) => chooseSkill(skill.slug, level)}
                  />
                ))}
              </ul>
            )}
          </section>
        </>
      ) : (
        <Callout tone="info">
          {terms.role === "superadmin"
            ? "A superadmin belongs to no tenant, so they have no campaigns or skills."
            : "Campaigns and skills belong to the phone the ACD rings. They will be available once account setup loads."}
        </Callout>
      )}
    </>
  );
}
